/**
 * Feature 104: the Care Concierge chooser — now two options (form + paid
 * consultation) after the free intro tier was removed. Rendered only on the
 * public /care-concierge page; in-app entry points open the form directly.
 *
 * Layout/hierarchy per the design reference: equal flat cards
 * (title → badges → BEST FOR → YOU GET → arrow CTA), no recommended badge,
 * no footer copy; "send details" swaps in place to the existing form with a
 * back affordance; the consultation tier opens Calendly.
 */
import { useState } from 'react';
import { trackRumAction } from '@/shared/observability/rum';
import { CareConciergeForm } from '@/features/dashboard/care-seeker/components/CareConciergeForm';
import type { PageOrigin } from '@/features/provider-directory/components/CareConciergContactForm/types';
import type { ConsultationTier } from '../conciergeCalendly';
import {
  BACK_TO_OPTIONS,
  CHOOSER_HEADING,
  CHOOSER_OPTIONS,
  type ChooserOption,
  type ChooserOptionId,
} from './conciergeChooserCopy';

type ConciergeChooserOptionsProps = {
  source: PageOrigin;
  onOpenCalendly: (tier: ConsultationTier) => void;
  /** Called when the details form succeeds (chooser hosts may close). */
  onFormSuccess?: () => void;
  onFormClose?: () => void;
};

const badgeClasses: Record<ChooserOption['badges'][number]['tone'], string> = {
  free: 'bg-[#026857]/10 text-[#026857]',
  time: 'bg-gray-100 text-gray-600',
  price: 'bg-[#E2A807]/25 text-[#7a5c00] font-bold',
};

const MicroLabel = ({ children }: { children: string }) => (
  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">{children}</p>
);

const YouGet = ({ option }: { option: ChooserOption }) => {
  if (!option.youGetEmphasis) return <p className="text-sm text-gray-600">{option.youGet}</p>;
  const [before, after] = option.youGet.split(option.youGetEmphasis);
  return (
    <p className="text-sm text-gray-600">
      {before}
      <strong className="text-gray-900">{option.youGetEmphasis}</strong>
      {after}
    </p>
  );
};

export function ConciergeChooserOptions({
  source,
  onOpenCalendly,
  onFormSuccess,
  onFormClose,
}: ConciergeChooserOptionsProps) {
  const [view, setView] = useState<'chooser' | 'form'>('chooser');

  const selectOption = (id: ChooserOptionId) => {
    trackRumAction('concierge_option_selected', { source, option: id });
    if (id === 'details') {
      trackRumAction('concierge_form_opened', { source });
      setView('form');
      return;
    }
    onOpenCalendly(id);
  };

  if (view === 'form') {
    return (
      <div>
        <button
          type="button"
          className="mb-3 text-sm font-semibold text-[#026857] underline-offset-2 hover:underline"
          onClick={() => setView('chooser')}
        >
          {BACK_TO_OPTIONS}
        </button>
        <CareConciergeForm
          pageOrigin={source}
          onSuccess={() => onFormSuccess?.()}
          onClose={() => onFormClose?.()}
        />
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-5 font-recoleta text-2xl font-semibold text-[#111827]">
        {CHOOSER_HEADING}
      </h2>
      <div className="grid items-stretch gap-3 sm:grid-cols-2">
        {CHOOSER_OPTIONS.map((option) => (
          <div
            key={option.id}
            role="button"
            tabIndex={0}
            onClick={() => selectOption(option.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                selectOption(option.id);
              }
            }}
            className="flex cursor-pointer flex-col gap-3 rounded-xl border border-gray-200 bg-white p-5 transition-all duration-150 hover:-translate-y-0.5 hover:border-[#026857]/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#026857]"
          >
            <h3 className="font-recoleta text-lg font-semibold text-[#111827]">{option.title}</h3>
            <div className="flex flex-wrap gap-1.5">
              {option.badges.map((badge) => (
                <span
                  key={badge.label}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClasses[badge.tone]}`}
                >
                  {badge.label}
                </span>
              ))}
            </div>
            <div>
              <MicroLabel>Best for</MicroLabel>
              <p className="text-sm text-gray-600">{option.bestFor}</p>
            </div>
            <div>
              <MicroLabel>You get</MicroLabel>
              <YouGet option={option} />
            </div>
            <p className="mt-auto pt-1 text-sm font-semibold text-[#026857]">{option.cta}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
